import React, { useState } from 'react';
import './PartnersTable.css';
import PartnerDetailsModal from '../PartnerDetailsModal/PartnerDetailsModal';

const PartnersTable = ({ partners, pagination, onPageChange, onRevokePartner }) => {
    const [sortField, setSortField] = useState('fechaRegistro');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedPartner, setSelectedPartner] = useState(null);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedPartners = [...partners].sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Manejo especial para estadísticas
        if (sortField.includes('estadisticas.')) {
            const statField = sortField.split('.')[1];
            aValue = a.estadisticas[statField];
            bValue = b.estadisticas[statField];
        }

        if (sortDirection === 'asc') {
            return aValue > bValue ? 1 : -1;
        } else {
            return aValue < bValue ? 1 : -1;
        }
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const getRoleBadge = (role) => {
        const roleColors = {
            'admin': 'badge--admin',
            'proveedor': 'badge--provider',
            'comerciante': 'badge--merchant',
            'moderador': 'badge--moderator',
            'soporte': 'badge--support'
        };

        return (
            <span className={`role-badge ${roleColors[role] || 'badge--default'}`}>
                {role.toUpperCase()}
            </span>
        );
    };

    const renderPagination = () => {
        const pages = [];
        const maxVisible = 5;
        let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
        let endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Botón anterior
        if (pagination.current_page > 1) {
            pages.push(
                <button
                    key="prev"
                    onClick={() => onPageChange(pagination.current_page - 1)}
                    className="pagination-btn"
                >
                    ←
                </button>
            );
        }

        // Páginas
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    className={`pagination-btn ${i === pagination.current_page ? 'active' : ''}`}
                >
                    {i}
                </button>
            );
        }

        // Botón siguiente
        if (pagination.current_page < pagination.last_page) {
            pages.push(
                <button
                    key="next"
                    onClick={() => onPageChange(pagination.current_page + 1)}
                    className="pagination-btn"
                >
                    →
                </button>
            );
        }

        return pages;
    };

    if (partners.length === 0) {
        return (
            <div className="partners-table">
                <div className="no-partners">
                    <h3>👥 No hay partners registrados</h3>
                    <p>Los usuarios convertidos a partners aparecerán aquí.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="partners-table">
            <div className="table-header">
                <h2>👥 Partners Registrados</h2>
                <div className="table-info">
                    <span>{pagination.total} partners totales</span>
                </div>
            </div>

            <div className="table-container">
                <table className="partners-table__table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('nombre')} className="sortable">
                                Nombre {sortField === 'nombre' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('email')} className="sortable">
                                Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th>Teléfono</th>
                            <th onClick={() => handleSort('rol')} className="sortable">
                                Rol {sortField === 'rol' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('fechaRegistro')} className="sortable">
                                Registro {sortField === 'fechaRegistro' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('estadisticas.total_ganancias')} className="sortable">
                                Ganancias {sortField === 'estadisticas.total_ganancias' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('estadisticas.total_referidos')} className="sortable">
                                Referidos {sortField === 'estadisticas.total_referidos' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('estadisticas.enlaces_activos')} className="sortable">
                                Enlaces {sortField === 'estadisticas.enlaces_activos' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedPartners.map((partner) => (
                            <tr key={partner.id}>
                                <td>
                                    <div className="partner-name">
                                        <strong>{partner.nombre}</strong>
                                        {partner.estadisticas.es_proveedor && (
                                            <span className="provider-badge">🏪 Proveedor</span>
                                        )}
                                    </div>
                                </td>
                                <td className="email-cell">{partner.email}</td>
                                <td>{partner.telefono || 'No registrado'}</td>
                                <td>{getRoleBadge(partner.rol)}</td>
                                <td>{formatDate(partner.fechaRegistro)}</td>
                                <td className="currency-cell">
                                    {formatCurrency(partner.estadisticas.total_ganancias)}
                                </td>
                                <td className="number-cell">
                                    {partner.estadisticas.total_referidos}
                                </td>
                                <td className="number-cell">
                                    {partner.estadisticas.enlaces_activos}
                                </td>
                                <td>
                                    <span className="status-badge status-badge--active">
                                        ✅ Activo
                                    </span>
                                </td>
                                <td>
                                    <div className="actions">
                                        <button
                                            onClick={() => setSelectedPartner({
                                                id: partner.id,
                                                name: partner.nombre
                                            })}
                                            className="btn-action btn-action--view"
                                            title="Ver detalles del partner"
                                        >
                                            👁️
                                        </button>
                                        <button
                                            onClick={() => onRevokePartner(partner.id)}
                                            className="btn-action btn-action--revoke"
                                            title="Revocar partner"
                                        >
                                            ❌
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {pagination.last_page > 1 && (
                <div className="pagination">
                    <div className="pagination-info">
                        Página {pagination.current_page} de {pagination.last_page}
                        ({pagination.total} registros)
                    </div>
                    <div className="pagination-controls">
                        {renderPagination()}
                    </div>
                </div>
            )}

            {/* Modal de detalles del partner */}
            {selectedPartner && (
                <PartnerDetailsModal
                    partnerId={selectedPartner.id}
                    partnerName={selectedPartner.name}
                    onClose={() => setSelectedPartner(null)}
                />
            )}
        </div>
    );
};

export default PartnersTable;
